// Per-anchor role-list constants for Turbo Sales. Carves three sub-role
// gates out of today's umbrella `sales` / `trainer` / `marketing` /
// `ads_guidance_editor` / `admin` / `owner` tokens.
//
// Spec: docs/design/role_taxonomy.md §4 (token list) + §5.1
// (umbrella-token implication map) + §6 (per-anchor module home).
//
// Each list includes the new sub-role token FIRST, then the umbrella
// tokens that should still satisfy the gate (backward-compat per §5.1),
// then the platform escape hatches. The arrays are intentionally plain
// `string[]` (not `as const`) to match the shape of `TAB_ACCESS` in
// `LeadWorkspaceHeaderAccess.ts`; nothing mutates them.

/**
 * Sales-rep gate: Sales-role view of the role-scoped `salesIntake`
 * panel (in-person lead-capture / "submit as deal" flow); future
 * dock surfaces tagged `bucketRelevance: "sales"` for rep-only
 * affordances.
 *
 * Granting `sales_rep` to a user who does NOT hold the umbrella
 * `sales` token is the new affordance — they can submit deals from
 * the Sales-role intake variant without seeing the rest of the Sales
 * surfaces a `sales`-tier user gets. Today's `sales` token holders
 * still pass via the §5.1 implication map.
 */
export const SALES_REP_ROLES: string[] = [
  "sales_rep",
  "sales",
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  "dev",
  "platform_owner",
  "platform_admin",
  "platform_dev",
];

/**
 * Sales-manager gate: rep-oversight tabs in Location Tools, future
 * Sales-anchor admin home (sales-org KPIs), Sales-side review queues.
 *
 * Carved separately from `admin` so an org can grant rep oversight
 * without granting the full tenant-admin umbrella. Today's `admin` /
 * `owner` token holders still pass via the §5.1 implication map.
 */
export const SALES_MANAGER_ROLES: string[] = [
  "sales_manager",
  "admin",
  "owner",
  "org_admin",
  "org_owner",
  "dev",
  "platform_owner",
  "platform_admin",
  "platform_dev",
];

/**
 * Training-author gate: roleplay-scenario authoring inside the
 * Manager-bucket `trainingAuthor` mode; org-level roleplay flags.
 *
 * Mirrors `canEditTrainingRoleplayScenarios` in
 * `src/lib/training/trainingReviewRoles.ts` (kept in sync — both
 * gate the same authoring capability). The new `training_author`
 * token is a refinement that lets an org grant authoring rights
 * without granting the full `trainer` role; the symmetric
 * `training_reviewer` carve is deferred per role-taxonomy §10 #5.
 */
export const TRAINING_AUTHOR_ROLES: string[] = [
  "training_author",
  "trainer",
  "marketing",
  "admin",
  "owner",
  "dev",
  "org_admin",
  "org_owner",
  "platform_owner",
  "platform_trainer",
  "platform_admin",
  "platform_dev",
];
